import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer} from "react";
import {arrayMove} from "@dnd-kit/sortable";

import * as api from "../../../../services/api";
import {apartmentMapper, apartmentPhotoMapper, apartmentServiceJoinMapper} from "../../../../mapper/apartmentMapper";
import {useFeedbackTools} from "../../../../components/FeedbackTools";
import lepri from "../../../../services/lepri-js";
import {useHome} from "../HomeContext";


const ApartmentContext = createContext();
export const useApartment = () => useContext(ApartmentContext);

export default function ApartmentProvider({apartmentId, children}) {
    const {refreshHome, refreshApartment} = useHome();
    const {showErrorNotification} = useFeedbackTools;

    const [apartmentState, apartmentDispatcher] = useReducer(
        (state, action) => {
            switch (action.type){
                case "setAttributes":
                    return {...state, ...action.attributes};
                case 'createPhoto':
                    return {...state, photos: [...state.photos, action.photo]};
                case 'movePhoto':
                    return {...state, photos: arrayMove(state.photos, action.oldIndex, action.newIndex)}
                case 'deletePhoto':
                    return {...state, photos: state.photos.filter((p) => p.id !== action.photoId)};
                default:
                    throw new Error(`Cannot find action ${action.type} for apartmentState reducer`);
            }
        },
        {
            isLoading: true,
        }
    );

    useEffect(
        () => {
            const loadData = async () => {
                if (apartmentId) {
                    try {
                        apartmentDispatcher({type: "setAttributes", attributes: {isLoading: true}});
                        const data = await api.Apartments.getApartment(apartmentId);
                        apartmentDispatcher({type: "setAttributes", attributes: {isLoading: false, ...apartmentMapper(data)}})
                    } catch (e) {
                        showErrorNotification(e);
                        apartmentDispatcher({type: "setAttributes", attributes: {isLoading: false}})
                    }
                }
            };
            loadData();
        },
        [apartmentId, showErrorNotification]
    )

    const updateApartment = useCallback(
        async (data) => {
            const res = await api.Apartments.updateApartment(apartmentId, data);
            apartmentDispatcher({type: "setAttributes", attributes: apartmentMapper(res)});
            refreshHome();
        },
        [apartmentId, refreshApartment]
    )

    const updateIcon = useCallback(
        async (file) => {
            const image = await lepri(file);
            const formData = new FormData();
            formData.append("icon", image);
            const apartmentData = await api.Apartments.updateApartmentIcon(apartmentId, formData);
            apartmentDispatcher({type: "setAttributes", attributes: apartmentMapper(apartmentData)})
            refreshApartment(apartmentData);
        },
        [apartmentId, refreshApartment]
    )

    const createPhoto = useCallback(
        async (file) => {
            const image = await lepri(file);
            const formData = new FormData();

            const maxOrderPosition = apartmentState.photos.reduce(
                (acc, val) => val.orderPosition > acc ? val.orderPosition : acc,
                0
            );

            formData.append("order_position", maxOrderPosition + 1);
            formData.append("title_it", apartmentState.name);
            formData.append("title_en", apartmentState.name);
            formData.append("photo", image);
            const res = await api.Apartments.createApartmentPhoto(apartmentId, formData)
            const photo = apartmentPhotoMapper(res)
            apartmentDispatcher({type: "createPhoto", photo: photo})
        },
        [apartmentId, apartmentState.photos, apartmentState.name]
    )

    const movePhoto = useCallback(
        async (photoId, oldIndex, newIndex) => {
            // first move the photo in the state to get an immediate visual response
            // then call the api to update the position
            apartmentDispatcher({type: "movePhoto", oldIndex: oldIndex, newIndex: newIndex});
            await api.Apartments.updateApartmentPhoto(photoId, {orderPosition: newIndex});
        },
        []
    )

    const deletePhoto = useCallback(
        async (photoId) => {
            await api.Apartments.deleteApartmentPhoto(photoId)
            apartmentDispatcher({type: "deletePhoto", photoId: photoId})
        },
        []
    )

    const updateApartmentServiceJoins = useCallback(
        async (data) => {
            const serviceJoins = await api.Apartments.updateApartmentServiceJoins(apartmentId, data);
            apartmentDispatcher({type:"setAttributes", attributes: {serviceJoins: serviceJoins.map(apartmentServiceJoinMapper)}})
        }, [apartmentId]
    )

    const infoScore = useMemo(
        () => {
            const checkAttr = (value) => {
                return !!value && value !== "" && (!Array.isArray(value) || value.length > 0)
            }

            let missingAttr = []
            let okAttr = []

            checkAttr(apartmentState.name) ? okAttr.push("Nome") : missingAttr.push("Nome");
            checkAttr(apartmentState.beds) ? okAttr.push("Posti letto") : missingAttr.push("Posti letto");
            checkAttr(apartmentState.icon) ? okAttr.push("Icona") : missingAttr.push("Icona");
            checkAttr(apartmentState.apartmentTypologyId) ? okAttr.push("Tipologia") : missingAttr.push("Tipologia");
            checkAttr(apartmentState.apartmentCategoryId) ? okAttr.push("Categoria") : missingAttr.push("Categoria");
            checkAttr(apartmentState.mq) ? okAttr.push("Superficie (metri quadrati)") : missingAttr.push("Superficie (metri quadrati)");
            checkAttr(apartmentState.numberOfBathroom) ? okAttr.push("Numero di bagni") : missingAttr.push("Numero di bagni");
            checkAttr(apartmentState.descriptionIt) ? okAttr.push("Descrizione (italiano)") : missingAttr.push("Descrizione (italiano)");
            checkAttr(apartmentState.descriptionEn) ? okAttr.push("Descrizione (inglese)") : missingAttr.push("Descrizione (inglese)");
            checkAttr(apartmentState.photos) ? okAttr.push("Foto interne") : missingAttr.push("Foto interne");
            checkAttr(apartmentState.serviceJoins) ? okAttr.push("Servizi") : missingAttr.push("Servizi");
            !apartmentState.cinCodeMissing || checkAttr(apartmentState.cinCode) ? okAttr.push("Codice CIN") : missingAttr.push("Codice CIN");

            return {
                score: `${okAttr.length}/${okAttr.length + missingAttr.length}`,
                missingAttribute: missingAttr
            }
        },
        [apartmentState]
    )

    const context = {
        isLoading: apartmentState.isLoading,
        id: apartmentState.id,
        name: apartmentState.name,
        beds: apartmentState.beds,
        sofaBeds: apartmentState.sofaBeds,
        extraBeds: apartmentState.extraBeds,
        descriptionIt: apartmentState.descriptionIt,
        descriptionEn: apartmentState.descriptionEn,
        apartmentTypologyId: apartmentState.apartmentTypologyId,
        apartmentCategoryId: apartmentState.apartmentCategoryId,
        mq: apartmentState.mq,
        numberOfBathroom: apartmentState.numberOfBathroom,
        visible: apartmentState.visible,
        icon: apartmentState.icon,
        serviceJoins: apartmentState.serviceJoins,
        photos: apartmentState.photos,
        cinCode: apartmentState.cinCode,
        propertyInstructions: apartmentState.propertyInstructions,
        infoScore,
        updateApartment,
        createPhoto,
        movePhoto,
        deletePhoto,
        updateApartmentServiceJoins,
        updateIcon,
    }

    return <ApartmentContext.Provider value={context}>
        {children}
    </ApartmentContext.Provider>
}